import { useEffect, useState } from "react"

/**
 * Retrieves the value stored in the local storage for the given key.
 * If a value is found, it is parsed from JSON and returned.
 * If no value is found, the provided default value is returned.
 *
 * @param {string} key - The key used to retrieve the value from local storage.
 * @param {T} defaultValue - The default value to return if no value is found.
 * @return {T} The value stored in local storage, or the default value.
 */
const getStorageValue = <T>(key: string, defaultValue: T): T => {
  const savedValue = localStorage.getItem(key)
  return (savedValue ? JSON.parse(savedValue) : defaultValue) as T
}

/**
 * Custom React hook that manages the state and persistence of a value in the local storage.
 *
 * @param {string} key - The key used to store and retrieve the value from the local storage.
 * @param {T} defaultValue - The default value to use if no value is found in the local storage.
 * @return {[T, React.Dispatch<React.SetStateAction<T>>]} An array containing the current value and a function to update it.
 */
const useLocaleStorage = <T>(key: string, defaultValue: T): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [value, setValue] = useState(() => {
    return getStorageValue(key, defaultValue)
  })

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value))
  }, [key, value])

  return [value, setValue]
}
export default useLocaleStorage
